import { isEqual } from "es-toolkit";
import { v4 as uuid } from "uuid";

import { Flatten, FlattenType } from "@/utils/types-utils";

// Better typed Object.fromEntries (original typing forces string keys on the output type)
export function fromEntries<K extends string | number | symbol, V>(entries: readonly [K, V][]): Record<K, V> {
  return Object.fromEntries(entries) as Record<K, V>;
}

export function keys<T extends string | number | symbol>(obj: {
  [k in T]?: unknown;
}): T[] {
  return Object.keys(obj) as T[];
}

export function entries<T extends string | number | symbol, V>(obj: {
  [k in T]?: V;
}): [T, V][] {
  return Object.entries(obj) as [T, V][];
}

export function mixin<TARGET extends object, SOURCE extends object>(
  target: TARGET,
  source: SOURCE,
  force: boolean = false,
): TARGET & SOURCE {
  for (const key of Object.keys(source)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((target as any)[key] === undefined || force) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (target as any)[key] = (source as any)[key];
    }
  }

  return target as TARGET & SOURCE;
}

export function makeCacheableData<T>(obj: T) {
  return {
    ...obj,
    [uuid()]: uuid(),
  };
}

export function resolvePath<T, K extends Flatten<T>>(
  object: T,
  path: Flatten<T>,
  defaultValue?: FlattenType<T, K>,
): FlattenType<T, K> {
  return (
    path
      .split(".")
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .reduce((o: any, p: any) => (o ? (o as any)[p] : defaultValue), object) as FlattenType<T, K>
  );
}

export function getDifferences<T extends Record<string, unknown>>(
  A: T | null | undefined,
  B: T | null | undefined,
): Partial<T> {
  if (!A && B) {
    return B;
  }

  if (A && !B) {
    return A;
  }

  if (!A && !B) {
    return {};
  }

  const differences: Partial<T> = {};

  const allKeys = new Set([...Object.keys(A!), ...Object.keys(B!)]);

  allKeys.forEach((key) => {
    if (typeof A![key] === "object" && typeof B![key] === "object") {
      if (!isEqual(A![key], B![key])) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (differences as any)[key] = B![key] !== undefined ? B![key] : A![key];
      }
    } else if (A![key] !== B![key]) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (differences as any)[key] = B![key] !== undefined ? B![key] : A![key];
    }
  });

  return differences;
}

export function isDefined<T>(x: T | undefined | null): x is T {
  return x !== undefined && x !== null;
}

/**
 * Returns the intersection of two sets (A - B)
 */
export function setDifference<T>(setA: Set<T>, setB: Set<T>): Set<T> {
  const difference = new Set<T>(setA);
  for (const elem of setB) {
    difference.delete(elem);
  }
  return difference;
}

/**
 * Type-safe narrowing of a value at runtime
 */
export function makeGuard<T, U extends T>(values: readonly U[]): (t: T) => t is U {
  return (t: T): t is U => values.includes(t as U);
}
